import { useCallback, useEffect, useRef } from 'react';

import { useIsTabAndWindowActive } from 'hooks/use-tab-active';
import { SIGNED_URL_EXPIRED_TIME } from 'constants/params';
import { RootState } from 'store/store';
import { useAppSelector, useAppDispatch, useAppStore } from 'store/store-hooks';
import { updFileUrls } from 'components/app/_mediator/app-files.slice';
import { updThumbUrls } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { updFcUrls } from 'components/app/finalcuts/_mediator/fc-data.slice';

const TIMEOUT = (SIGNED_URL_EXPIRED_TIME - 1) * 60 * 1000;

export const useAppUrls = () => {
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const isAudioPlaying = useAppSelector((state) => state.audio.isAudioPlaying);
  const isTabActive = useIsTabAndWindowActive();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const isReadyToRefresh = useRef(false);
  const listenTab = useRef(false);
  const timeout = useRef<NodeJS.Timeout>();

  const cb = useCallback(async () => {
    const { selectedStory, selectedFinalcut, selectedProject } = selectData(store.getState());
    if (!isReadyToRefresh.current) {
      listenTab.current = true;
      return;
    }
    if (!selectedProject || !selectedStory) return;
    await Promise.all([
      dispatch(updFileUrls(selectedProject)),
      dispatch(updThumbUrls(selectedStory)),
      selectedFinalcut && dispatch(updFcUrls(selectedFinalcut)),
    ]);
    wait();
  }, []);

  const wait = () => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(cb, TIMEOUT);
  };

  useEffect(() => {
    if (!selectedProject) return;
    wait();
    return () => clearTimeout(timeout.current);
  }, [selectedProject]);

  useEffect(() => {
    isReadyToRefresh.current = isTabActive && !isAudioPlaying;
    if (!isTabActive || isAudioPlaying || !listenTab.current) return;
    listenTab.current = false;
    cb();
  }, [isTabActive, isAudioPlaying]);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { selectedProject, selectedStory, selectedFinalcut } = state.projects;
  return { selectedProject, selectedStory, selectedFinalcut };
};
